/**
 * Pino logger configuration for Power Automate MCP
 *
 * Logs to stderr to avoid interfering with MCP stdio communication
 */

import pino, { type LoggerOptions } from "pino";

const isProduction = process.env["NODE_ENV"] === "production";
// Default to "info" level for security - debug logging may expose sensitive data
// Use LOG_LEVEL=debug explicitly when debugging is needed
const logLevel = process.env["LOG_LEVEL"] ?? "info";

// Build logger options based on environment
function getLoggerOptions(): LoggerOptions {
  const baseOptions: LoggerOptions = {
    name: "powerautomate-mcp",
    level: logLevel,
    redact: {
      paths: [
        "Authorization",
        "authorization",
        "accessToken",
        "token",
        "password",
        "secret",
        "refreshToken",
        "refresh_token",
        "idToken",
        "id_token",
        "client_secret",
        "apiKey",
        "api_key",
        "**.Authorization",
        "**.authorization",
        "**.accessToken",
        "**.token",
        "**.password",
        "**.secret",
        "**.refreshToken",
        "**.refresh_token",
        "**.idToken",
        "**.id_token",
        "**.client_secret",
        "**.apiKey",
        "**.api_key",
        "requestBody.properties.connectionReferences.*.connectionName",
      ],
      censor: "[REDACTED]",
    },
  };

  if (isProduction) {
    // Production: JSON output to stderr
    return baseOptions;
  }

  // Development: pretty print to stderr
  return {
    ...baseOptions,
    transport: {
      target: "pino-pretty",
      options: {
        colorize: true,
        destination: 2, // stderr
        ignore: "pid,hostname",
        translateTime: "SYS:HH:MM:ss",
      },
    },
  };
}

// Create base logger
// Note: MCP uses stdio, so we MUST log to stderr only (stdout is for JSON-RPC)
export const logger = pino(getLoggerOptions(), pino.destination(2));

/**
 * Create a child logger with additional context
 */
export function createChildLogger(bindings: Record<string, unknown>): pino.Logger {
  return logger.child(bindings);
}

/**
 * Interactive CLI commands (--setup, --login, --client) narrate their own
 * steps on the terminal; pino info lines (auth-flow progress, response-shape
 * diagnostics) interleave with the device-code prompt and read as noise.
 * Drop to warn for those commands — warnings and errors still show, and an
 * explicit LOG_LEVEL always wins. MCP server mode is unaffected.
 */
export function quietInteractiveLogging(): void {
  // Only step down from the default — an explicit LOG_LEVEL or a level
  // already changed by --debug wins.
  if (!process.env["LOG_LEVEL"] && logger.level === "info") {
    logger.level = "warn";
  }
}

/**
 * Log levels reference:
 * - fatal: Application crash
 * - error: Error that needs attention
 * - warn: Unexpected but handled condition
 * - info: Normal operation events
 * - debug: Detailed information for debugging
 * - trace: Very detailed tracing
 */

// Type exports for consumers
export type Logger = pino.Logger;
export type LogLevel = pino.Level;
